import React from 'react'

import { Dropdown, type DropdownOption } from '@/components/ui'
import { useApi } from '@/hooks'
import type { GeofenceDetails } from '@/types/dashboard-types'
import { type DataSource } from '@/types/data-source'

import { toQueryParam } from '../common/queryUtils'

interface Params {
  companyIds?: number[]
  geofenceIds?: string[]
  onChange: (geofenceIds?: string[]) => void
  /** Which back end to list geofences from; defaults to Fresh. */
  dataSource?: DataSource
}

export const GeofenceDropdown: React.FC<Params> = ({ geofenceIds, companyIds, onChange, dataSource }) => {
  const { api } = useApi()
  const [options, setOptions] = React.useState<DropdownOption[]>([])
  const [placeholder, setPlaceholder] = React.useState('')

  React.useEffect(() => {
    if (!api) {
      return
    }
    if (!companyIds || companyIds.length === 0) {
      setOptions([])
      setPlaceholder('Choose a company to load geofences...')
      return
    }
    const fn = async () => {
      setPlaceholder('Loading...')
      const response = await api.get<GeofenceDetails[]>({
        path: `/geofences?companyIds=${toQueryParam(companyIds)}&skip=0&take=10000&deleted=false&withDescendants=true${dataSource ? `&dataSource=${dataSource}` : ''}`,
        origin: 'GeofenceDropdown',
        expiresIn: '1h',
      })
      if (response.error || !response.data) {
        console.error(response.error)
        setPlaceholder('Error loading geofences. Contact support.')
        setOptions([])
      } else {
        setPlaceholder('Choose one or more geofences...')
        setOptions(
          response.data.map((a) => {
            return {
              label: a.name,
              value: String(a.id),
            }
          })
        )
      }
    }
    fn()
  }, [companyIds])

  return (
    <div>
      <label>Geofences</label>
      <Dropdown
        options={options}
        handleMultiSelectItem={(opt: DropdownOption[]) => {
          const selectedValues = opt.filter((option) => option.value !== '').map((option) => option.value)
          const geofenceIds = selectedValues.length > 0 ? selectedValues : undefined
          onChange(geofenceIds)
        }}
        selectedItem={options.filter((o) => Array.isArray(geofenceIds) && geofenceIds.includes(o.value))}
        placeholder={placeholder}
        animateDots={/^Loading/.test(placeholder)}
        isMultiSelect
      />
    </div>
  )
}
